// Modules
import React, { useEffect, useRef, useState } from 'react'
import { Form, FormikProps } from 'formik'
import { formatCPF } from '@brazilian-utils/brazilian-utils'

// Application
import { useSignUp } from 'context'
import { maskBirthDate } from 'utils'
import { genderIdentityKeys, genderListForSocialName } from 'constants/'

// Components
import { FieldWithButton, StyledForm } from './style'
import { SignUpFooter } from 'redesign/components/molecules/SignUpFooter'
import { FormikCustomField } from '../FormikCustomField'
import { HasSocialNameRadio } from '../HasSocialNameRadio'
import { FormikCustomCheckbox } from '../FormikCustomCheckbox'
import { FormikGenderCustomSelect } from '../FormikGenderCustomSelect'
import { GenderIdentityModalButton } from '../GenderIdentityModalButton'
import { BiologicalSexModalButton } from '../BiologicalSexModalButton'
import { BiologicalSexCustomSelect } from '../BiologicalSexCustomSelect'
import { LegalNameModalButton } from '../LegalNameModalButton'
import { RevenueDataInformation } from '../RevenueDataInformation'

export interface FormValues {
    name: string
    socialName: string
    cpf: string
    birthDate: string
    term: boolean
    genderIdentity: string
    hasSocialName?: boolean
    biologicalSex: string
}

type FormType = FormikProps<FormValues>

export const InnerForm: React.FC<FormType> = props => {
    const { touched, errors, values, handleSubmit } = props
    const { isLoading, personalData } = useSignUp()
    const [showRevenueDataInformation, setShowRevenueDataInformation] = useState(false)
    const formRefs = {
        cpf: useRef<HTMLInputElement>(null),
        name: useRef<HTMLInputElement>(null),
        birthDate: useRef<HTMLInputElement>(null),
        genderIdentity: useRef<HTMLDivElement>(null),
        hasSocialName: useRef<HTMLDivElement>(null),
        biologicalSex: useRef<HTMLInputElement>(null),
        terms: useRef<HTMLDivElement>(null),
        socialName: useRef<HTMLInputElement>(null),
    }

    useEffect(() => {
        const bringRevenueData = JSON.parse(sessionStorage.getItem('@LiviaAuth:BringRevenueData')!)
        setShowRevenueDataInformation(bringRevenueData)
    }, [])

    useEffect(() => {
        if (Object.keys(touched).length > 0) {
            if (touched.cpf && errors.cpf) {
                formRefs.cpf.current && formRefs.cpf.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
            if (touched.name && errors.name) {
                formRefs.name.current && formRefs.name.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
            if (touched.birthDate && errors.birthDate) {
                formRefs.birthDate.current && formRefs.birthDate.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
            if (touched.genderIdentity && errors.genderIdentity) {
                formRefs.genderIdentity.current && formRefs.genderIdentity.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
            if (touched.biologicalSex && errors.biologicalSex) {
                formRefs.biologicalSex.current && formRefs.biologicalSex.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
            if (touched.hasSocialName && errors.hasSocialName) {
                formRefs.hasSocialName.current && formRefs.hasSocialName.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
            if (touched.socialName && errors.socialName) {
                formRefs.socialName.current && formRefs.socialName.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
            if (touched.term && errors.term) {
                formRefs.terms.current && formRefs.terms.current.scrollIntoView({ behavior: 'smooth' })
                return
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors, touched])

    return (
        <StyledForm>
            <Form onSubmit={handleSubmit}>
                <div className="form__personal-data">
                    <FormikCustomField
                        className={`form__personal-data__field ${touched.cpf && errors.cpf ? 'underage' : ''}`}
                        dataTestId="cpf-input"
                        disabled={!!personalData?.name}
                        inputMode="numeric"
                        inputRef={formRefs.cpf}
                        label="CPF"
                        mask={formatCPF}
                        name="cpf"
                        type="tel"
                    />
                    <FieldWithButton>
                        <FormikCustomField
                            dataTestId="name-input"
                            disabled={!!personalData?.name}
                            inputRef={formRefs.name}
                            label="Nome legal"
                            name="name"
                        />
                        <LegalNameModalButton />
                    </FieldWithButton>
                    {showRevenueDataInformation && !!personalData?.name && <RevenueDataInformation />}

                    <FormikCustomField
                        dataTestId="birthDate-input"
                        disabled={!!personalData?.birthDate}
                        inputMode="numeric"
                        inputRef={formRefs.birthDate}
                        label="Data de nascimento"
                        mask={maskBirthDate}
                        name="birthDate"
                        type="tel"
                    />
                    {showRevenueDataInformation && !!personalData?.birthDate && <RevenueDataInformation />}

                    <FieldWithButton ref={formRefs.genderIdentity}>
                        <FormikGenderCustomSelect name="genderIdentity" />
                        <GenderIdentityModalButton />
                    </FieldWithButton>

                    {values.genderIdentity === genderIdentityKeys.NAO_BINARIO && (
                        <FieldWithButton ref={formRefs.biologicalSex}>
                            <BiologicalSexCustomSelect dataTestId="biological-sex-input" name="biologicalSex" />
                            <BiologicalSexModalButton />
                        </FieldWithButton>
                    )}

                    {genderListForSocialName.includes(values.genderIdentity) && (
                        <HasSocialNameRadio radioRef={formRefs.hasSocialName} title="Quer informar seu nome social?" />
                    )}

                    {genderListForSocialName.includes(values.genderIdentity) && values.hasSocialName && (
                        <FormikCustomField
                            dataTestId="social-name-input"
                            inputRef={formRefs.socialName}
                            label="Qual seu nome social?"
                            name="socialName"
                        />
                    )}

                    <FormikCustomCheckbox dataTestId="term-checkbox" id="terms" name="term" termsRef={formRefs.terms} />
                </div>
                <SignUpFooter isSubmitButtonLoading={isLoading} submitButtonText="Continuar cadastro" />
            </Form>
        </StyledForm>
    )
}
